import * as React from 'react';
import clsx from 'clsx';
import { Button, Icon, LoaderScreen, Typography } from '@/components/common';
import Icons from '@/const/icons';
import { FollowUpList, QuestionType } from '@/interfaces';
import { FilterQuestionCard } from './FilterQuestionCard';
import { FilterHeaderBar } from './FilterHeaderBar';
import { useRouter } from 'next/router';

// ─── Types ──────────────────────────────────────────────────────────────────

export type FilterMode = 'include' | 'exclude';

export interface FollowUpAnswer {
	id: string;
	text: string;
	detail?: string;
	isCorrect?: boolean;
}

export interface FilterQuestion {
	id: number;
	questionType: QuestionType;
	question: string;
	answers: FollowUpAnswer[];
}

export interface SelectedAnswer {
	followUpId: number;
	followUpName: string;
	questionId: number;
	questionText: string;
	answerId: string;
	answerText: string;
}

// ─── Main component ───────────────────────────────────────────────────────────

interface DocumentFilterPanelProps {
	followUpsList: FollowUpList[];
	isLoading?: boolean;
	sidebarTitle?: string;
	onApply?: (filters: {
		mode: FilterMode;
		selectedAnswers: SelectedAnswer[];
	}) => void;
	onCancel?: () => void;
}

export const DocumentFilterPanel: React.FC<DocumentFilterPanelProps> = ({
	onApply,
	isLoading,
	followUpsList,
	sidebarTitle = 'Follow-ups',
}) => {
	const router = useRouter();
	const { documentFilter } = router.query;
	const [selectedFollowUpId, setSelectedFollowUpId] = React.useState<
		number | null
	>(null);
	const [filterMode, setFilterMode] = React.useState<FilterMode>('include');

	// Map of followUpId -> questionId -> Set of answerIds
	const [selections, setSelections] = React.useState<
		Record<number, Record<number, Set<string>>>
	>({});
	const [hydrated, setHydrated] = React.useState(false);

	const followUps = followUpsList ?? [];

	// ── Hydrate selections from the persisted documentFilter query param ────
	React.useEffect(() => {
		if (hydrated || followUps.length === 0 || !documentFilter) {
			setHydrated(true);
			return;
		}

		try {
			const persisted = JSON.parse(documentFilter as string) as {
				questionId: number;
				answers: string[];
			}[];

			const restored: Record<number, Record<number, Set<string>>> = {};

			for (const { questionId, answers } of persisted) {
				// Find which follow-up owns this question
				const ownerFollowUp = followUps.find((fu) =>
					fu.questions?.some((q) => q.id === questionId)
				);
				if (!ownerFollowUp) continue;

				if (!restored[ownerFollowUp.id]) restored[ownerFollowUp.id] = {};
				restored[ownerFollowUp.id][questionId] = new Set(answers);
			}

			if (Object.keys(restored).length > 0) {
				setSelections(restored);
			}
		} catch {
			// malformed param – ignore
		}

		setHydrated(true);
	}, [followUps, documentFilter, hydrated]);

	// Track the previous follow-up IDs to detect list changes (e.g. new follow-up added)
	const prevFollowUpIdsRef = React.useRef<string>('');

	React.useEffect(() => {
		if (!followUpsList || followUpsList.length === 0) return;

		const currentIdsKey = followUpsList.map((fu) => fu.id).join(',');

		// Reset to first item on initial load OR when the list changes
		if (
			selectedFollowUpId === null ||
			prevFollowUpIdsRef.current !== currentIdsKey
		) {
			setSelectedFollowUpId(followUpsList[0].id);
		}

		prevFollowUpIdsRef.current = currentIdsKey;
	}, [followUpsList]); // eslint-disable-line react-hooks/exhaustive-deps

	const currentFollowUp = followUps.find((fu) => fu.id === selectedFollowUpId);
	const currentQuestions: FilterQuestion[] = React.useMemo(() => {
		if (!currentFollowUp) return [];
		return currentFollowUp.questions?.map((q) => ({
			id: q.id,
			questionType: q.type,
			question: q.text,
			answers:
				q.options?.map((opt) => ({
					id: opt.id !== undefined ? opt.id.toString() : opt.text,
					text: opt.text,
					detail: opt.detail,
					isCorrect: opt.is_correct,
				})) || [],
		}));
	}, [currentFollowUp]);

	// ── Toggle an answer ────────────────────────────────────────────────────
	const handleToggleAnswer = (
		followUpId: number,
		questionId: number,
		answerId: string
	) => {
		setSelections((prev) => {
			const fuMap = { ...(prev[followUpId] ?? {}) };
			const answerSet = new Set(fuMap[questionId] ?? []);

			if (answerSet.has(answerId)) answerSet.delete(answerId);
			else answerSet.add(answerId);

			return {
				...prev,
				[followUpId]: { ...fuMap, [questionId]: answerSet },
			};
		});
	};

	// ── Build flat list of selected answers for chips ───────────────────────
	const allSelectedAnswers = React.useMemo<SelectedAnswer[]>(() => {
		const result: SelectedAnswer[] = [];
		for (const followUp of followUps) {
			const fuSelections = selections[followUp.id];
			if (!fuSelections) continue;

			for (const question of followUp.questions) {
				const answerSet = fuSelections[question.id];
				if (!answerSet || answerSet.size === 0) continue;

				for (const answerId of Array.from(answerSet)) {
					// DATE questions store a synthetic combined range string
					// (e.g. "05-01-2026 - 05-12-2026") that won't match any
					// individual option.text, so we push it directly.
					if (question.type === 'DATE') {
						result.push({
							followUpId: followUp.id,
							followUpName: followUp.name,
							questionId: question.id,
							questionText: question.text,
							answerId,
							answerText: answerId,
						});
						continue;
					}

					const option = question.options?.find((opt) =>
						opt.id !== undefined
							? opt.id.toString() === answerId
							: opt.text === answerId
					);
					if (option) {
						result.push({
							followUpId: followUp.id,
							followUpName: followUp.name,
							questionId: question.id,
							questionText: question.text,
							answerId,
							answerText: option.text,
						});
					}
				}
			}
		}
		return result;
	}, [selections, followUps]);

	// ── Chips: group by follow-up ────────────────────────────────────────────
	const chipsByFollowUp = React.useMemo(() => {
		const map: Record<number, { name: string; count: number }> = {};
		for (const sel of allSelectedAnswers) {
			if (!map[sel.followUpId]) {
				map[sel.followUpId] = { name: sel.followUpName, count: 0 };
			}
			map[sel.followUpId].count++;
		}
		return map;
	}, [allSelectedAnswers]);

	// ── Remove all answers for a follow-up ──────────────────────────────────
	const handleRemoveFollowUpChip = (followUpId: number) => {
		setSelections((prev) => {
			const next = { ...prev };
			delete next[followUpId];
			return next;
		});
	};

	// ── Apply ───────────────────────────────────────────────────────────────
	const handleApply = () => {
		onApply?.({ mode: filterMode, selectedAnswers: allSelectedAnswers });
	};

	return (
		<div className="flex flex-col h-full overflow-hidden rounded-b-[32px]">
			{/* ── Main body ── */}
			<div className="flex flex-1 overflow-hidden">
				{/* Left panel: Follow-ups list */}
				<div className="w-[280px] flex-shrink-0 flex flex-col gap-2 bg-grey-5 pt-7 pb-8 border-t border-grey-100 overflow-y-auto scroll-custom">
					<Typography
						type="semibold-14"
						className="text-black uppercase mb-1 tracking-wide text-left border-b pb-7 px-6"
					>
						{sidebarTitle}
					</Typography>

					<ul className="space-y-3 px-4">
						{isLoading && <LoaderScreen />}
						{!isLoading && followUps.length === 0 && (
							<Typography type="regular-16">No follow-ups found</Typography>
						)}
						{followUps.map((fu) => {
							const isActive = fu.id === selectedFollowUpId;
							return (
								<li key={fu.id}>
									<button
										type="button"
										onClick={() => setSelectedFollowUpId(fu.id)}
										className={clsx(
											'w-full flex gap-2.5 px-3 py-2.5 rounded-xl text-left transition-colors duration-150',
											isActive
												? 'bg-primary text-white'
												: 'text-opacity-black hover:bg-grey-100/50'
										)}
									>
										<Icon
											src={Icons.documentFile}
											width={16}
											height={16}
											className={clsx(
												'flex-shrink-0',
												isActive ? 'text-white' : 'text-opacity-black'
											)}
										/>
										<Typography
											type="regular-14"
											className={clsx(
												'flex-1 text-left leading-snug font-medium',
												isActive ? 'text-white' : 'text-opacity-black'
											)}
										>
											{fu.name}
										</Typography>
									</button>
								</li>
							);
						})}
					</ul>
				</div>

				{/* Right panel: Questions */}
				<div className="flex-1 flex flex-col overflow-hidden bg-grey-2 border-l border-grey-100">
					<FilterHeaderBar
						filterMode={filterMode}
						setFilterMode={setFilterMode}
						chipsByFollowUp={chipsByFollowUp}
						handleRemoveFollowUpChip={handleRemoveFollowUpChip}
					/>
					{/* Questions scroll area */}
					<div className="flex-1 overflow-y-auto scroll-custom px-8 py-4 space-y-4 shadow-shadow2">
						{currentQuestions.map((question, idx) => {
							const selectedAnswerIds = Array.from(
								(selectedFollowUpId !== null
									? selections[selectedFollowUpId]?.[question.id]
									: null) ?? []
							);
							return (
								<FilterQuestionCard
									key={question.id}
									question={question}
									questionIndex={idx + 1}
									selectedAnswerIds={selectedAnswerIds}
									onToggleAnswer={(answerId) =>
										handleToggleAnswer(
											selectedFollowUpId as number,
											question.id,
											answerId
										)
									}
								/>
							);
						})}
					</div>

					{/* Footer */}
					<div className="flex items-center justify-end gap-3 p-4 border-t border-grey-100 bg-white rounded-br-[32px]">
						<Button
							onClick={handleApply}
							size="medium"
							decoration="primary"
							label="Apply Filter"
							disabled={allSelectedAnswers.length === 0}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};
